import type { MessageToolMailboxData } from "@virtual-brain-lib/types";

import { ToolCard, ToolCardSteps } from "../core";
import { getMailboxConnectionHeader, ToolCardMailboxConnect } from "./ToolCardMailboxConnect";
import { ToolCardMailboxList } from "./ToolCardMailboxList";
import { useMailboxConnection } from "./useMailboxConnection";

export interface ToolCardMailboxProps {
	readonly state: MessageToolMailboxData;
	readonly className?: string;
}

export function ToolCardMailbox({ state, className }: ToolCardMailboxProps) {
	const { status, statusText, steps, messages, contentReferenceOffset, connection } = state;
	const { connectionState, connectMailbox } = useMailboxConnection(connection);

	if (status !== "processing" && messages.length === 0 && steps.length === 0) {
		return null;
	}

	const getDefaultStatusText = () => {
		if (status === "processing") {
			return "Searching your mailbox";
		}

		const count = messages.length;

		if (count === 0) {
			return "No emails found";
		}

		return `Found ${count} email${count > 1 ? "s" : ""}`;
	};

	const getCompactSummary = () => {
		if (status === "processing") {
			return "Searching your mailbox";
		}

		const count = messages.length;

		if (count === 0) {
			return "No emails found";
		}

		return `${count} email${count > 1 ? "s" : ""} found`;
	};

	if (connection && connectionState) {
		const {
			statusText: connectionStatusText,
			compactSummary,
			statusIcon,
		} = getMailboxConnectionHeader(connectionState, connection.provider);

		return (
			<ToolCard
				className={className}
				compactSummary={compactSummary}
				icon="envelop"
				status={status}
				statusIcon={statusIcon}
				statusText={connectionStatusText}
				title="Mailbox"
			>
				<ToolCardMailboxConnect
					connectionState={connectionState}
					provider={connection.provider}
					onConnect={connectMailbox}
				/>
			</ToolCard>
		);
	}

	return (
		<ToolCard
			className={className}
			compactSummary={getCompactSummary()}
			icon="envelop"
			status={status}
			statusText={statusText ?? getDefaultStatusText()}
			title="Mailbox"
		>
			{steps.length > 0 && <ToolCardSteps steps={steps} />}
			<ToolCardMailboxList
				messages={messages}
				contentReferenceOffset={contentReferenceOffset}
			/>
		</ToolCard>
	);
}
